import React, { useContext, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, useParams } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { HeaderContext } from '../../LayoutWithLeftSidebar/HeaderContext'
import { ScheduledQueryBackHeader } from './ScheduledQueryBackHeader'
import '../Results/CrossClientResults.scss'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { addToast } from '../../../store/toast/toastSlice'
import { useGetScheduledCrossClientQueryQuery, useUpdateScheduledCrossClientQueryMutation } from '../../../store/crossClient/scheduledCrossClient'
import { Spinner } from '../../../components/Spinner/Spinner'
import { NotAuthorizedPageContent } from '../../../components/NotAuthorizedPageContent'
import { useZocksAuth } from '../../../components/ScopedBlock/hooks'
import { ScheduledQueryForm } from './ScheduledQueryForm'
import { ScheduledQueryDetailsCard } from './ScheduledQueryDetailsCard'
import { cronExpressionsToFrequency, frequencyToCronExpressions, type ScheduledQueryFrequency } from './scheduledQueryFrequency'
import {
  DISABLED_SCHEDULED_AUTOMATION,
  normalizeScheduledAutomation,
  type ScheduledCrossClientQueryAutomation,
} from '../../../models/ScheduledCrossClientQuery'
import './ScheduledQuery.scss'

export const EditScheduledQueryView: React.FC = () => {
  const { scheduledQueryId } = useParams()

  if (!scheduledQueryId) {
    return <div>No scheduled query provided</div>
  }

  // Key by query so all form state resets when navigating between scheduled queries
  return <EditScheduledQueryContent key={scheduledQueryId} scheduledQueryId={scheduledQueryId} />
}

interface EditScheduledQueryContentProps {
  scheduledQueryId: string
}

const EditScheduledQueryContent: React.FC<EditScheduledQueryContentProps> = ({ scheduledQueryId }) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { portalRef } = useContext(HeaderContext)

  const accountId = useAppSelector(selectAccountId)
  const currentUser = useAppSelector(selectSafeAuthenticatedUser)
  const { hasScope } = useZocksAuth()
  const canEdit = hasScope('**', 'schd_ccq:update', ['schd_ccq:admin'])

  const { data: scheduledQuery, isLoading, isError, error } = useGetScheduledCrossClientQueryQuery({ accountId, scheduledQueryId }, { skip: !canEdit })
  const isNotFound = isError && typeof error === 'object' && 'status' in error && error.status === 404

  const [updateScheduledQuery, { isLoading: updating }] = useUpdateScheduledCrossClientQueryMutation()

  const [name, setName] = useState('')
  const [runAsUserId, setRunAsUserId] = useState(currentUser.id)
  const [frequency, setFrequency] = useState<ScheduledQueryFrequency | null>(null)
  const [automation, setAutomation] = useState<ScheduledCrossClientQueryAutomation | null>(null)

  useEffect(() => {
    if (!scheduledQuery) {
      return
    }

    setName(scheduledQuery.name)
    setRunAsUserId(scheduledQuery.managedBy.id)
    setFrequency(cronExpressionsToFrequency(scheduledQuery.schedule.cronExpressions))
    setAutomation(normalizeScheduledAutomation(scheduledQuery.automation ?? DISABLED_SCHEDULED_AUTOMATION))
  }, [scheduledQuery])

  if (!canEdit) {
    return <NotAuthorizedPageContent />
  }

  if (isLoading || (scheduledQuery && (!frequency || !automation))) {
    return (
      <div className='d-flex flex-column align-items-center justify-content-center gap-3 mt-10'>
        <Spinner />
      </div>
    )
  }

  if (isError && !isNotFound) {
    return <div className='paragraph-medium zocks-text-gray-700 p-4'>Failed to load the scheduled query. Please try again.</div>
  }

  if (isNotFound || !scheduledQuery || !frequency || !automation) {
    return <div className='paragraph-medium zocks-text-gray-700 p-4'>Scheduled query not found</div>
  }

  const goBack = () => navigate('/cross-client?tab=scheduled')

  const handleSubmit = async () => {
    try {
      await updateScheduledQuery({
        accountId,
        scheduledQueryId,
        request: {
          name: name.trim(),
          schedule: { cronExpressions: frequencyToCronExpressions(frequency) },
          automation,
        },
      }).unwrap()
      dispatch(addToast({ variant: 'success', title: 'Success', messages: ['The scheduled query has been updated.'] }))
    } catch (e) {
      Sentry.captureException(e)
      dispatch(addToast({ variant: 'error', title: 'Error', messages: ['Failed to update the scheduled query. Please try again.'] }))
    }
  }

  return (
    <div className='truly-full-size-content d-flex cross-client-results-container'>
      {/* eslint-disable-next-line react-hooks/refs -- pre-existing app-wide HeaderContext pattern: portal target ref is read during render */}
      {portalRef && portalRef.current ? createPortal(<ScheduledQueryBackHeader to='/cross-client?tab=scheduled' />, portalRef.current) : null}
      <div className='d-flex align-items-start zocks-p-2.0 scheduled-query-layout'>
        <div className='d-flex flex-column zocks-gap-2 scheduled-query-main'>
          <h3 className='mb-0'>Edit scheduled query</h3>
          <ScheduledQueryForm
            name={name}
            onNameChange={setName}
            runAsUserId={runAsUserId}
            onRunAsUserIdChange={setRunAsUserId}
            frequency={frequency}
            onFrequencyChange={setFrequency}
            automation={automation}
            onAutomationChange={setAutomation}
            runAsDisabled
            submitLabel='Save changes'
            submitting={updating}
            onSubmit={handleSubmit}
            onCancel={goBack}
          />
        </div>
        <div className='scheduled-query-rail'>
          <ScheduledQueryDetailsCard details={scheduledQuery.definition} />
        </div>
      </div>
    </div>
  )
}
